import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { faArrowTrendUp } from '@fortawesome/pro-solid-svg-icons/faArrowTrendUp';
import { faArrowTrendDown } from '@fortawesome/pro-solid-svg-icons/faArrowTrendDown';
import { faDash } from '@fortawesome/pro-solid-svg-icons/faDash';
import { colorPalette } from 'shared/constants';
import { TREND_STATUS } from 'shared/constants/quality-metric.constants';
import {
  calculateCases,
  displayGoal,
  displayPercentage,
  formatTrendPeriod,
  isSuccess,
} from 'shared/utils/quality-metrics.utils';
import { MetricGraph } from 'src/components/quality-metric/quality-metric-card/graph-over-time';
import { Divider } from 'src/components/design-system';
import { formatNumber } from 'shared/utils';
import { useQualityMetricsContext } from '../../quality-metrics-context';
import styles from './overview-tab.module.scss';

const TREND_ICON = {
  [TREND_STATUS.UP]: faArrowTrendUp,
  [TREND_STATUS.DOWN]: faArrowTrendDown,
  [TREND_STATUS.NO_CHANGE]: faDash,
};

function getTrendLabel(trend) {
  if (!trend || trend.status === TREND_STATUS.NA) return null;
  if (trend.status === TREND_STATUS.NO_CHANGE) return '0%';
  const sign = trend.delta > 0 ? '+' : '';
  return `${sign}${trend.delta}%`;
}

export function OverviewTab() {
  const {
    qualityMetric,
    userMetric,
    userTrendDelta,
    groupTrendDelta,
    processedUserTrends,
    processedGroupTrends,
    goalPercentage,
    selectedMetricId,
    lang,
  } = useQualityMetricsContext();

  const isUserSuccess = isSuccess({
    total: userMetric.totalRecords,
    success: userMetric.totalSuccess,
    groupGoal: userMetric.groupGoal,
  });

  const isGroupSuccess = isSuccess({
    total: qualityMetric.totalGroupRecords,
    success: qualityMetric.totalGroupSuccess,
    groupGoal: qualityMetric.groupGoal,
  });

  const { userCases, departmentCases } = calculateCases({ qualityMetric, userMetric });

  const goalDisplay = displayGoal(userMetric);

  const currentLabel = formatTrendPeriod(userTrendDelta?.currentMonth, userTrendDelta?.currentYear);
  const comparisonLabel = formatTrendPeriod(
    userTrendDelta?.comparisonMonth,
    userTrendDelta?.comparisonYear,
  );
  const trendVsLabel =
    currentLabel && comparisonLabel
      ? lang.TREND_TOOLTIP.replace('{currentPeriod}', currentLabel).replace(
          '{previousPeriod}',
          comparisonLabel,
        )
      : null;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.subheader}>
          <span className={styles.goalText}>
            {lang.GOAL} {goalDisplay}
          </span>
          {trendVsLabel && <span className={styles.trendLabel}>{trendVsLabel}</span>}
        </div>

        <Divider />

        <div className={styles.scores}>
          <ScoreDisplay
            label={lang.MY_CASES}
            percentage={displayPercentage({
              total: userMetric.totalRecords,
              success: userMetric.totalSuccess,
              invert: userMetric.invert,
            })}
            caseCount={`${formatNumber(userCases)} ${lang.OF} ${formatNumber(userMetric.totalRecords)} ${lang.CASES}`}
            isSuccess={isUserSuccess}
            trend={userTrendDelta}
          />

          <ScoreDisplay
            label={lang.DEP_CASES}
            percentage={displayPercentage({
              total: qualityMetric.totalGroupRecords,
              success: qualityMetric.totalGroupSuccess,
              invert: qualityMetric.invert,
            })}
            caseCount={`${formatNumber(departmentCases)} ${lang.OF} ${formatNumber(qualityMetric.totalGroupRecords)} ${lang.CASES}`}
            isSuccess={isGroupSuccess}
            trend={groupTrendDelta}
          />
        </div>

        <Divider />

        <p className={styles.graphTitle}>{lang.CASES_OVER_TIME}</p>
        <MetricGraph
          goal={goalPercentage}
          invert={qualityMetric.invert}
          metricId={selectedMetricId}
          totalRecords={userMetric.totalRecords}
          minimumRecords={userMetric.minimumRecords}
          trends={processedUserTrends}
          groupTrends={processedGroupTrends}
          hideLabel
        />
      </div>

      {(qualityMetric.description || qualityMetric.compliance) && (
        <div className={styles.aboutColumn}>
          <AboutPanel qualityMetric={qualityMetric} lang={lang} />
        </div>
      )}
    </div>
  );
}

function ScoreDisplay({ label, percentage, caseCount, isSuccess: success, trend }) {
  const iconColor = success ? colorPalette.greenPositive : colorPalette.redNegative;
  const icon = success ? faCheckCircle : faTriangleExclamation;
  const trendLabel = getTrendLabel(trend);
  const trendIcon = trend && TREND_ICON[trend.status];

  return (
    <div className={styles.scoreBlock}>
      <span className={styles.scoreLabel}>{label}</span>
      <div className={styles.scoreRow}>
        <FontAwesomeIcon icon={icon} color={iconColor} className={styles.scoreIcon} />
        <span className={styles.scorePercentage} style={{ color: iconColor }}>
          {percentage}
        </span>
        {trendLabel && (
          <span className={styles.scoreTrend}>
            {trendLabel}
            {trendIcon && <FontAwesomeIcon icon={trendIcon} className={styles.scoreTrendIcon} />}
          </span>
        )}
      </div>
      <span className={styles.scoreCases}>{caseCount}</span>
    </div>
  );
}

function AboutPanel({ qualityMetric, lang }) {
  const { description, compliance, title } = qualityMetric;

  return (
    <div className={styles.aboutPanel}>
      <h3 className={styles.sectionTitle}>
        {lang.ABOUT_METRIC.replace('{metricName}', title.toLowerCase())}
      </h3>

      {description && <p className={styles.sectionText}>{description}</p>}

      {compliance && (
        <>
          <h3 className={styles.sectionTitle}>{lang.COMPLIANCE}</h3>
          <p className={styles.sectionText}>{compliance}</p>
        </>
      )}
    </div>
  );
}
